import React from 'react';
import { View, Text } from 'react-native';
import theme from '../../theme';

function Bars(props) {
  const data = props.data;
  const max = Math.max(...data.map((d) => d.value));

  return (
    <View style={{ width: '100%', gap: theme.spacing.space3 }}>
      {data.map((d, i) => (
        <View key={i} style={{ gap: 4 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontFamily: theme.typography.fontBody, fontSize: theme.typography.size.xs, color: theme.colors.textSecondary }}>
              {d.label}
            </Text>
            <Text style={{ fontFamily: theme.typography.fontMono, fontSize: theme.typography.size.xs, color: theme.colors.textPrimary }}>
              {d.value}%
            </Text>
          </View>
          <View style={{ height: 6, borderRadius: theme.radii.full, backgroundColor: theme.colors.borderSubtle, overflow: 'hidden' }}>
            <View
              style={{
                width: `${(d.value / max) * 100}%`,
                height: '100%',
                borderRadius: theme.radii.full,
                backgroundColor: d.color || theme.colors.accentPrimary,
              }}
            />
          </View>
        </View>
      ))}
    </View>
  );
}

function Stat(props) {
  const data = props.data;
  const max = Math.max(...data.sparkline);

  return (
    <View style={{ width: '100%', gap: theme.spacing.space3 }}>
      <Text
        style={{
          fontFamily: theme.typography.fontMono,
          fontSize: theme.typography.size['3xs'],
          letterSpacing: theme.trackingPx(theme.typography.size['3xs'], 'widest'),
          textTransform: 'uppercase',
          color: theme.colors.textTertiary,
        }}
      >
        {data.label}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: theme.spacing.space2 }}>
        <Text style={{ fontFamily: theme.typography.fontDisplay, fontSize: theme.typography.size['3xl'], color: theme.colors.textPrimary }}>
          {data.value}
        </Text>
        <View
          style={{
            backgroundColor: theme.colors.statusPositiveTint,
            borderRadius: theme.radii.full,
            paddingHorizontal: theme.spacing.space2,
            paddingVertical: 2,
          }}
        >
          <Text style={{ fontFamily: theme.typography.fontMono, fontSize: theme.typography.size['3xs'], color: theme.colors.statusPositive }}>
            {data.delta}
          </Text>
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 4, height: 48, width: '100%' }}>
        {data.sparkline.map((v, i) => (
          <View
            key={i}
            style={{
              flex: 1,
              height: `${(v / max) * 100}%`,
              borderRadius: 2,
              backgroundColor: i === data.sparkline.length - 1 ? theme.colors.accentPrimary : theme.colors.borderStrong,
            }}
          />
        ))}
      </View>
    </View>
  );
}

function MiniTable(props) {
  const data = props.data;

  return (
    <View style={{ width: '100%' }}>
      <View
        style={{
          flexDirection: 'row',
          borderBottomWidth: theme.borderWidth.thin,
          borderBottomColor: theme.colors.borderDefault,
          paddingBottom: theme.spacing.space2,
        }}
      >
        {data.columns.map((c, i) => (
          <Text
            key={i}
            style={{
              flex: 1,
              fontFamily: theme.typography.fontMono,
              fontSize: theme.typography.size['3xs'],
              letterSpacing: theme.trackingPx(theme.typography.size['3xs'], 'wide'),
              textTransform: 'uppercase',
              color: theme.colors.textTertiary,
            }}
          >
            {c}
          </Text>
        ))}
      </View>
      {data.rows.map((row, ri) => (
        <View
          key={ri}
          style={{
            flexDirection: 'row',
            paddingVertical: theme.spacing.space2,
            borderBottomWidth: theme.borderWidth.thin,
            borderBottomColor: theme.colors.borderSubtle,
          }}
        >
          {row.map((cell, ci) => (
            <Text
              key={ci}
              style={{ flex: 1, fontFamily: theme.typography.fontMono, fontSize: theme.typography.size.xs, color: theme.colors.textPrimary }}
            >
              {cell}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
}

function Chat(props) {
  const data = props.data;

  return (
    <View style={{ width: '100%', gap: theme.spacing.space3 }}>
      <View
        style={{
          alignSelf: 'flex-end',
          backgroundColor: theme.colors.gray50,
          borderWidth: theme.borderWidth.thin,
          borderColor: theme.colors.borderDefault,
          borderRadius: theme.radii.md,
          borderBottomRightRadius: 2,
          paddingVertical: theme.spacing.space2,
          paddingHorizontal: theme.spacing.space3,
          maxWidth: '85%',
        }}
      >
        <Text style={{ fontFamily: theme.typography.fontBody, fontSize: theme.typography.size.sm, color: theme.colors.textPrimary }}>
          {data.question}
        </Text>
      </View>
      <View
        style={{
          alignSelf: 'flex-start',
          backgroundColor: theme.colors.brandBlueTint,
          borderRadius: theme.radii.md,
          borderBottomLeftRadius: 2,
          padding: theme.spacing.space3,
          maxWidth: '92%',
        }}
      >
        <Text style={{ fontFamily: theme.typography.fontBody, fontSize: theme.typography.size.sm, color: theme.colors.textPrimary }}>
          {data.answer}
        </Text>
      </View>
    </View>
  );
}

/**
 * ServiceVisual — small, real-looking data preview for a service panel.
 * Swap-in replacement for a plain icon tile: pass `variant` + shape-matched
 * `data` and it renders the right mini visualization.
 *
 *   variant: 'bars'  data: [{ label, value(0-100), color? }]
 *   variant: 'stat'  data: { label, value, delta, sparkline: number[] }
 *   variant: 'table' data: { columns: string[], rows: string[][] }
 *   variant: 'chat'  data: { question, answer }
 */
export function HomeServiceVisual(props) {
  const variant = props.variant;
  const data = props.data;

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 360,
        aspectRatio: 16 / 10,
        backgroundColor: theme.colors.surfaceCard,
        borderWidth: theme.borderWidth.thin,
        borderColor: theme.colors.borderDefault,
        borderRadius: theme.radii.lg,
        padding: theme.spacing.space5,
        justifyContent: 'center',
      }}
    >
      {variant === 'bars' && <Bars data={data} />}
      {variant === 'stat' && <Stat data={data} />}
      {variant === 'table' && <MiniTable data={data} />}
      {variant === 'chat' && <Chat data={data} />}
    </View>
  );
}

export default HomeServiceVisual;
